import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Alert, Box, TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Paper, useTheme, Pagination, TextField, Typography, IconButton } from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import { Add } from '@mui/icons-material';

import { useAuth } from '../../Auth/AuthContext';

import RowTableUsuarios from '../SubComponents/Usuarios/RowTableUsuarios';
import DialogCreateUsuario from '../SubComponents/Usuarios/DialogCreateUsuario';
import EmptyState from '../common/EmptyState';
import { getTopSearchMatchIds } from '../../utils/searchHighlights';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const API_URL = process.env.REACT_APP_API_URL;

export const getUserDeleteErrorDetail = (error) => getApiErrorMessage(
  error,
  'No fue posible eliminar el usuario.',
  ['detail']
);

function bubbleSort(arr) {
  let n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j].id < arr[j + 1].id) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]]; // Ordenar por ID de forma descendente
      }
    }
  }
  return arr;
}

function Usuarios() {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;

  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  
  const handleCloseDialogCreate = () => { setOpenDialogCreate(false) }
  const handleRefreshData = () => { setRefresh(prev => !prev); };

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    axios.get(`${API_URL}/usuarios/`)
      .then(response => {
        const filteredUsersIfYoureAEmployed = response.data.filter(usuario => usuario.nombre === user.nombre);
        const visibleUsers = areUserAdmin ? response.data : filteredUsersIfYoureAEmployed;
        setUsuarios(bubbleSort([...visibleUsers]));
      })
      .catch(error => {
        console.error('Error fetching usuarios:', error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const handleDeleteUsuario = async (id) => {
    setDeleteError('');
    try {
      const response = await axios.delete(`${API_URL}/usuarios/${id}/`);
      if (response.status !== 204) {
        setDeleteError('No fue posible eliminar el usuario.');
        return;
      }
      setUsuarios(prevUsuarios => prevUsuarios.filter(usuario => usuario.id !== id));
    } catch (error) {
      console.error('Error deleting usuario:', error);
      setDeleteError(getUserDeleteErrorDetail(error));
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredUsuarios = usuarios.filter(usuario =>
    usuario.id.toString().includes(searchTerm) ||
    usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.rol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentUsuarios = bubbleSort(filteredUsuarios.slice(startIndex, startIndex + rowsPerPage));
  const searchHighlightIds = useMemo(() => new Set(getTopSearchMatchIds(
    currentUsuarios,
    searchTerm,
    ['id', 'nombre', 'rol']
  )), [currentUsuarios, searchTerm]);
  const handleUpdateData = (updatedUsuario) => { setUsuarios((prevData) => prevData.map((usuario) => usuario.id === updatedUsuario.id ? updatedUsuario : usuario));};

  return (
    <Box sx={{ paddingTop: 1, position: 'relative', height: '100%' }}>
      <Typography variant='h5' color='grey'>TOTAL DE USUARIOS: {usuarios.length}</Typography>
      {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
      <TextField 
        label="Buscar Usuario Por Cualquier Dato"
        variant="outlined"
        sx={{ paddingBottom: 2, paddingTop: 0 }}
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={handleSearchChange}
      />

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead sx={{
            backgroundColor: isDarkMode ? grey[800] : blue[700],
            color: isDarkMode ? 'black' : 'white',
          }}>
            <TableRow>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>ID</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>NOMBRE</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', borderRight: areUserAdmin ? null : '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>ROL</TableCell>
              { areUserAdmin && <TableCell align="center"sx={{borderLeft: '2px solid #ddd',borderBottom: '2px solid #ddd',borderRight: '2px solid #ddd',fontWeight: 'bold',color: 'white',verticalAlign: 'middle'}}>
                ACCIONES
                <IconButton onClick={() => {setOpenDialogCreate(true)}}>
                    <Add sx={{ color: 'white' }}/>
                </IconButton>
              </TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && currentUsuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={areUserAdmin ? 4 : 3}>
                  <EmptyState message={loadError ? 'No fue posible cargar los usuarios.' : searchTerm ? 'No se encontraron resultados.' : 'No hay usuarios para mostrar.'} />
                </TableCell>
              </TableRow>
            ) : currentUsuarios.map((item) => (
              <RowTableUsuarios key={item.id} data={item} handleDelete={handleDeleteUsuario} handleUpdateData={handleUpdateData} areUserAdmin={areUserAdmin} searchHighlighted={searchHighlightIds.has(item.id)}/>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Pagination 
          count={Math.ceil(usuarios.length / rowsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      { areUserAdmin && <DialogCreateUsuario openDialog={openDialogCreate} handleCloseDialog={handleCloseDialogCreate} setOpenDialog={setOpenDialogCreate} handleRefreshData={handleRefreshData}/> }
    </Box>
  );
}

export default Usuarios;
